"use client";

import { useLayoutEffect } from "react";
import { THEME_QUERY, THEME_STORAGE_KEY, resolveTheme } from "@/lib/theme";

/**
 * The theme, before React first paints, on the documents the boot script
 * cannot reach (TA-343, moved out of `Sidebar.tsx`; TA-252 wrote the effect).
 *
 * `app/layout.tsx` sets `data-theme` from an inline script in <head> before
 * any pixel is drawn, and where that script ran this component does nothing.
 * A `notFound()` thrown from a layout (today `contacts/[id]/layout.tsx`) is
 * served as Next's `__next_error__` document instead, which the client
 * renders with `createRoot`: the script arrives as React markup and never
 * runs, and React clears the attributes on <html> as it takes the element
 * over. Before TA-252 the theme landed in ThemeSwitch's passive effect, after
 * the first paint, so a dark-mode operator saw the frame paint light and fade
 * to dark through the sidebar's colour transitions (TA-247 kept the page off
 * the design lint for it).
 *
 * A layout effect runs after React has written the DOM and before the browser
 * paints, so the first styles the frame ever gets are the right theme's and
 * nothing has a colour to transition from.
 *
 * WHY THE ROOT LAYOUT, NOT THE SIDEBAR. TA-252 put this in `Sidebar.tsx`
 * because the root layout is a server component and no client file was in
 * scope, which covered only pages inside `(operator)`. Since TA-209 this app
 * also serves `(public)` pages a stranger reads, and a `notFound()` thrown
 * there, or from any future route group, would paint light first. A root
 * client component covers every document the app renders, which is what the
 * fallback was always for.
 *
 * WHY NOT `components/shell/`. That directory is the operator frame, and
 * TA-209's fence (`legal/publicRoutes.test.ts`) forbids the root layout, which
 * wraps the public legal pages too, from importing any of it. This component
 * belongs to the document rather than to either frame, so it sits outside
 * both. The fence caught the first placement of this file.
 *
 * Not covered: before the client bundle runs, the error document has no
 * stylesheet and no markup (TA-247's probe), so nothing here reaches whatever
 * the browser shows of that blank document.
 */
export function ThemeBeforePaint(): null {
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (root.dataset["theme"]) return;
    let saved: string | null = null;
    try {
      saved = localStorage.getItem(THEME_STORAGE_KEY);
    } catch {
      /* storage unavailable: the OS decides, as in the boot script */
    }
    root.dataset["theme"] = resolveTheme(saved, matchMedia(THEME_QUERY).matches);
  }, []);
  return null;
}
